/*
 * Copyright (c) 2021 EGT Digital - All rights reserved.
 *
 * File: Grid.tsx
 *
 * Written by Yuliyan Milenkov <yuliyan.milenkov@egt-digital.com> on  05.07.22 2:45 pm.
 * Last modified: 05.07.22 2:45 pm.
 *
 * More information at: https://egt-digital.com/
 */
import React, { useEffect, useRef, useState } from 'react';
import { debounceTime, fromEvent } from 'rxjs';
import { getSectionScrollPossition } from '../api';
import { shouldShowScrollers } from '../api/helpers';
import Scroller from '../components/Scroller';
import { useSettings } from '../contexts/SettingsContext';
import store from '../eventStore';

const Grid = ({ count, children, maxRows, className, name }: IGridProps) => {
    const [settings] = useSettings();
    const [hovered, setIsHovered] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const [showLeftScroller, setShowLeftScroller] = useState(false);
    const [showRightScroller, setShowRightScroller] = useState(false);
    const { left: scrollStorageLeftPossition } = getSectionScrollPossition(name);
    const [scrollPossition, setScrollPosition] = useState((maxRows || 0) < 3 ? scrollStorageLeftPossition : 0);

    useEffect(() => {
        if (containerRef.current) {
            const { shouldShowLeft, shouldShowRight } = shouldShowScrollers(containerRef.current, scrollPossition);
            setShowLeftScroller(shouldShowLeft);
            setShowRightScroller(shouldShowRight);
        }
    }, [hovered, containerRef, scrollPossition, maxRows, name]);

    useEffect(() => {
        if (containerRef.current) {
            if ((maxRows || 0) < 3 && containerRef.current.scrollLeft !== scrollPossition) {
                containerRef.current.scrollLeft = scrollPossition;
            }
        }
    }, [containerRef, scrollPossition, maxRows, name]);

    useEffect(() => {
        const subscription = store.SCROLL_STORAGE.subscribe(data => {
            const { left: leftPos } = getSectionScrollPossition(name);
            if (data.sections[name] && data.sections[name].left) {
                setScrollPosition(leftPos);
            }
        });
        return () => subscription.unsubscribe();
    }, [name]);

    useEffect(() => {
        if (containerRef.current && (maxRows || 0) < 3) {
            const subscription = fromEvent(containerRef.current, 'scroll')
                .pipe(debounceTime(400))
                .subscribe(e => {
                    // @ts-ignore
                    const leftScroll = e.target.scrollLeft;
                    store.SCROLL_SECTION.next({
                        name,
                        type: 'left',
                        value: leftScroll,
                    });
                });
            return () => subscription.unsubscribe();
        }
    }, [containerRef, scrollPossition, maxRows, name]);

    const scrollLeft = () => {
        if (containerRef && containerRef.current) {
            const scroll = containerRef.current.scrollLeft + window.innerWidth / 2;
            containerRef.current.scrollLeft = scroll;
            setScrollPosition(scroll);
        }
    };

    const scrollRight = () => {
        if (containerRef && containerRef.current) {
            const scroll = containerRef.current.scrollLeft - window.innerWidth / 2;
            containerRef.current.scrollLeft = scroll;
            setScrollPosition(scroll);
        }
    };

    return (
        <div
            className={`egt_section_grid_wrapper ${className || ''}`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div
                className={`egt_section_grid ${!maxRows && count > 0 ? 'egt_single_section_grid' : ''}`}
                style={
                    (maxRows && {
                        gridTemplateColumns: `repeat(${
                            count > maxRows ? Math.ceil(count / maxRows) : count
                        },var(--grid-cell-width))`,
                    }) ||
                    {}
                }
                ref={containerRef}
            >
                {children}
            </div>
            {Boolean(maxRows) && hovered && showRightScroller && settings.mode === 'desktop' && (
                <Scroller variant="right" onClick={scrollLeft} />
            )}
            {Boolean(maxRows) && hovered && showLeftScroller && settings.mode === 'desktop' && (
                <Scroller variant="left" onClick={scrollRight} />
            )}
        </div>
    );
};

export interface IGridProps {
    count: number;
    maxRows?: number;
    className?: string;
    name: string;
    children?: React.ReactNode;
}

export default Grid;
